import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useTranslation } from 'react-i18next';
import {
  filterSortData,
  getSampleRequestsTodoList,
  exportToPdf,
  exportExcel,
  setSampleSelectedIds,
  getSampleStatus,
  saveBulkTrackingDates,
  saveBulkSamplePriority,
  saveBulkSampleStatus,
} from './store/TodoList.action';
import List from './List';
import NewSearchBar from '../../../../Components/Utility/FilterAction/NewSearchBar';
import FilterSampleTodoList from '../FilterTodoList/FilterSampleTodoList';
import { Page_Size } from '../../../../Components/helper';
import { getSampleTraking } from '../../../DevlopmentOrder/store/DevelopmentOrder.action';
import '../Todo.css';
import BulkDatesModal from './BulkDatesModal';
import { useComponent } from '../../../UserSetting/UsersLibrary/UserRights';
import { toasterMsg } from '../../../../Components/Toaster/Toaster';
import Loader from '../../../../Components/Utility/Loader/Loader';
import { SamplesRootState, SamplesContainerState, SampleRow, ProductBrand, TrackingDateField, BulkSampleStatus } from './store/Samples.type';
import { getDefaultSampleStatusIds } from './default-sample-status-filter';

/**
 * Resolve a stable brand identifier for a selected sample row.
 * Prefers the brand ObjectId (brand[0]._id); falls back to the brand name (brandId).
 */
const getRowBrandKey = (row: SampleRow): string | null => (row as Record<string, any>)?.brand?.[0]?._id || row?.brandId || null;

/** Read a sample row's status name (sampleStatus is an array or object). */
const getRowStatusName = (row: SampleRow): string => {
  const raw = row?.sampleStatus;
  return (Array.isArray(raw) ? raw[0]?.name : raw?.name) || '';
};

/** Only Pending samples can have their tracking dates bulk-updated. */
const isRowPending = (row: SampleRow): boolean => getRowStatusName(row).toLowerCase() === 'pending';

/**
 * Normalise a DB statusColor value (e.g. 'gray', 'green', 'red') to the bulk-status bar
 * colour class used in Todo.css. Colours stay dynamic (driven by SampleStatus.statusColor);
 * this only maps spelling variants and provides an amber fallback.
 */
const BULK_STATUS_COLOR_MAP: Record<string, string> = {
  green: 'green',
  red: 'red',
  amber: 'amber',
  yellow: 'amber',
  gray: 'grey',
  grey: 'grey',
  teal: 'teal',
  blue: 'teal',
};
const resolveStatusBarClass = (statusColor?: string): string => `bulk-status-bar--${BULK_STATUS_COLOR_MAP[String(statusColor || '').toLowerCase()] ?? 'amber'}`;

/**
 * Compute the set of applicable tracking-date fields (refKeys) for a brand,
 * as a stable, comparable signature. A field applies to a brand when its
 * `brands` array contains that brand id. Brands with no scoping fall back to
 * all active fields, matching the single/bulk tracking modals.
 */
const getBrandFieldSignature = (brandKey: string | null, trackingMasters?: TrackingDateField[]): string => {
  if (!Array.isArray(trackingMasters)) return '';
  const refKeys = trackingMasters
    .filter((td) => td?.isActive)
    .filter((td) => {
      if (Array.isArray(td.brands) && td.brands.length > 0) {
        return brandKey ? td.brands.includes(brandKey) : false;
      }
      return true;
    })
    .map((td) => td.refKey)
    .filter(Boolean);
  return [...new Set(refKeys)].sort().join('|');
};

function Samples() {
  const dispatch = useDispatch();
  const [dateShow, setDateShow] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);

  React.useEffect(() => {
    if (!exportMenuOpen) return undefined;
    const close = (e: MouseEvent) => {
      if ((e.target as HTMLElement)?.closest?.('.floating-export-menu')) return;
      setExportMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [exportMenuOpen]);

  const { t } = useTranslation('common');
  const [lengthEnabled, setLengthEnabled] = useState(true);

  const { logInRights } = useSelector((s: SamplesRootState) => s.Users);
  const { CheckActionPermission } = useComponent();
  const sampleAddEditAccess = CheckActionPermission(logInRights, 'products', 'samples' as any, 'addEdit' as any);
  const UserDetails = useSelector((s: SamplesRootState) => s.UserDetails);
  const isSupplierLoggedIn = Boolean(UserDetails?.isSupplierLoggedIn);
  const canBulkUpdateSamples = Boolean(sampleAddEditAccess) && !isSupplierLoggedIn;
  const { sampleTrakingDates } = useSelector((s: SamplesRootState) => s.Measuremets);
  const {
    sampleTodoList = [],
    filterValues: {
      searchText = '',
      pageIndex = 1,
      pageSize,
      selectedSeason,
      selectedYear,
      selectedBrand,
      selectedSubBrand,
      selectedSupplier,
      selectedSampleType,
      selectedColour,
      selectedColourGroup,
      selectedProductType,
      selectedTrackingStatus,
      selectedSampleStatus,
      selectedCategory1,
      selectedCategory2,
      selectedCategory3,
      selectedCategory4,
      sortOrder,
      sortPropertyName,
    },
    sampleTodoListTotalCount = 0,
    sampleSelectedIds = [],
  } = useSelector((s: SamplesRootState) => s.SampleTodoListData);

  const [state, setState] = useState<SamplesContainerState>({
    samplesTodoList: [],
    isLoading: true,
    hasMore: true,
    loading: true,
    totalRecords: null,
    searchLoaded: false,
    isExportLoading: false,
    isDownloadLoading: false,
    selectedCount: 0,
  });
  const [selectedRows, setSelectedRows] = useState<SampleRow[]>([]);
  const [bulkBrand, setBulkBrand] = useState<ProductBrand[] | null>(null);
  const [bulkPriorityMode, setBulkPriorityMode] = useState(false);
  const [copyPriority, setCopyPriority] = useState<string | null>(null);
  const [capturedRowId, setCapturedRowId] = useState<string | null>(null);
  const [clearSelectionToken, setClearSelectionToken] = useState(0);
  const [bulkStatusMenuOpen, setBulkStatusMenuOpen] = useState(false);
  const [bulkStatusApplying, setBulkStatusApplying] = useState(false);
  const [statusList, setStatusList] = useState<BulkSampleStatus[]>([]);

  /**
   * Stable identity (empty deps — only state setters are used) so the `onCapturePriority`
   * prop does not change every render. An unstable identity here cascades through List's
   * `onPriorityPillChange` into its `columnDefs` memo, rebuilding every AG Grid column on
   * each parent render — the source of the on-scroll re-render jank.
   */
  const handleCapturePriority = useCallback((priority: string | null, rowId: string | null = null) => {
    setCopyPriority(priority);
    setCapturedRowId(rowId);
  }, []);

  const commonFilterParams = useMemo(
    () => ({
      seasons: selectedSeason,
      year: selectedYear,
      brand: selectedBrand,
      subBrand: selectedSubBrand || [],
      supplier: selectedSupplier,
      sampleType: selectedSampleType,
      colour: selectedColour,
      colorGroupId: selectedColourGroup || [],
      productTypeId: Array.isArray(selectedProductType) ? selectedProductType : [],
      trackingStatus: selectedTrackingStatus,
      status: selectedSampleStatus,
      category1: selectedCategory1,
      category2: selectedCategory2,
      category3: selectedCategory3,
      category4: selectedCategory4,
    }),
    [
      selectedSeason,
      selectedYear,
      selectedBrand,
      selectedSubBrand,
      selectedSupplier,
      selectedSampleType,
      selectedColour,
      selectedColourGroup,
      selectedProductType,
      selectedTrackingStatus,
      selectedSampleStatus,
      selectedCategory1,
      selectedCategory2,
      selectedCategory3,
      selectedCategory4,
    ],
  );

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('loggedInUserInfo') as string);
    if (userData?.lengthVisible !== null) setLengthEnabled(userData?.lengthVisible);
  }, []);

  useEffect(() => {
    const callFn = async () => {
      setState((prevState) => ({ ...prevState, isLoading: true }));
      const statuses = (await (dispatch(getSampleStatus({ isActive: true })) as unknown as Promise<BulkSampleStatus[]>)) || [];
      setStatusList(statuses);
      let filterd = {};
      const obj = JSON.parse(localStorage.getItem('sampleTodoListFilter') as string);
      if (obj) {
        filterd = {
          seasons: obj.selectedSeason,
          year: obj.selectedYear,
          brand: obj.selectedBrand,
          subBrand: obj.selectedSubBrand || [],
          supplier: obj.selectedSupplier,
          sampleType: obj.selectedSampleType,
          colour: obj.selectedColour,
          colorGroupId: obj.selectedColourGroup || [],
          productTypeId: Array.isArray(obj.selectedProductType) ? obj.selectedProductType : [],
          trackingStatus: obj.selectedTrackingStatus,
          status: obj.selectedSampleStatus,
          category1: obj.selectedCategory1,
          category2: obj.selectedCategory2,
          category3: obj.selectedCategory3,
          category4: obj.selectedCategory4,
        };
        await dispatch(filterSortData(obj) as any);
      } else {
        const defaultStatusIds = getDefaultSampleStatusIds(statuses);
        if (defaultStatusIds.length > 0) {
          filterd = { status: defaultStatusIds };
          await dispatch(filterSortData({ selectedSampleStatus: defaultStatusIds } as any));
        }
      }
      const params = { pageSize, pageIndex: 1, type: 'samples', ...filterd };
      await dispatch(getSampleRequestsTodoList(params));
      setState((prevState) => ({ ...prevState, isLoading: false }));
    };
    callFn();
  }, [dispatch]);

  const handlePageNextInScroll = async () => {
    let index = pageIndex;
    index += 1;
    const params = {
      pageIndex: index,
      pageSize,
      searchText,
      totalRecords: sampleTodoListTotalCount,
      isScrolled: true,
      type: 'samples',
      ...commonFilterParams,
      loading: true,
      sortOrder,
      sortPropertyName,
    };
    const obj = { pageIndex: index, pageSize };
    setState((prevState) => ({ ...prevState, isLoading: true }));
    await dispatch(filterSortData(obj as any));
    await dispatch(getSampleRequestsTodoList(params));
    setState((prevState) => ({ ...prevState, isLoading: false }));
  };

  useEffect(() => {
    if (sampleTodoListTotalCount > sampleTodoList?.length) setState((s) => ({ ...s, hasMore: true }));
    else setState((s) => ({ ...s, hasMore: false }));
  }, [sampleTodoList?.length, sampleTodoListTotalCount]);

  const handleSearch = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const targetValue = e.target.value;
    setState((prevState) => ({ ...prevState, isLoading: true, searchLoaded: true }));
    await dispatch(filterSortData({ searchText: targetValue } as any));
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (state.searchLoaded) {
        const params = {
          searchText,
          pageIndex: 1,
          pageSize: Page_Size,
          type: 'samples',
          ...commonFilterParams,
        };
        await dispatch(getSampleRequestsTodoList(params));
        setState((s) => ({ ...s, isLoading: false, searchLoaded: false }));
      }
    }, 1000);
    return () => clearTimeout(delayDebounceFn);
  }, [searchText, dispatch]);

  const exportFiles = async (type: string) => {
    const params = {
      searchText,
      pageIndex: 1,
      pageSize: Page_Size,
      type: 'samples',
      ...commonFilterParams,
      sampleIds: sampleSelectedIds,
      downloadType: type,
      lengthEnabled,
      sortOrder,
      sortPropertyName,
    };
    if (type === 'pdf') {
      setState((prevState) => ({ ...prevState, isDownloadLoading: true }));
      await dispatch(exportToPdf(params));
      setState((prevState) => ({ ...prevState, isDownloadLoading: false }));
    } else {
      setState((prevState) => ({ ...prevState, isExportLoading: true }));
      await dispatch(exportExcel(params));
      setState((prevState) => ({ ...prevState, isExportLoading: false }));
    }
    dispatch(setSampleSelectedIds([]));
    setClearSelectionToken((token) => token + 1);
  };

  /** Stable identity so the `getSelectedCount` prop does not re-fire List's selection effect every render. */
  const getCount = useCallback((selectedCnt: SampleRow[]) => {
    setSelectedRows(selectedCnt);
    setState((prevState) => ({ ...prevState, selectedCount: selectedCnt.length }));
  }, []);

  useEffect(() => {
    dispatch(getSampleTraking());
  }, [dispatch]);

  const showPopup = () => {
    if (sampleSelectedIds.length === 0) return;
    const signatures = selectedRows.map((row) => getBrandFieldSignature(getRowBrandKey(row), sampleTrakingDates));
    const uniqueSignatures = new Set(signatures);
    if (uniqueSignatures.size > 1) {
      toasterMsg(t('samples:TAKING_DATES.DIFFERENT_DATES_SELECTED'), 'danger');
      return;
    }
    setBulkBrand((selectedRows[0] as Record<string, any>)?.brand ?? null);
    setDateShow(true);
  };

  const submitBulkSampleTracking = async (data: Record<string, any>) => {
    const updatedData: Record<string, unknown> = {};
    updatedData.parentId = null;
    updatedData.loggedInUserId = UserDetails.userInfo?.userId;
    updatedData.trackingDates = data.trackingDates.map((track: TrackingDateField) => ({
      label: `${track.name} Date`,
      value: data.requestType === 'request' && track.refKey === 'requested' ? null : track.value || null,
      trackingUrl: track.trackingUrl || null,
      sampleId: track?._id,
      key: track.refKey || track.key,
    }));
    updatedData.dueDate = data.dueDate;
    const bulkData = (sampleSelectedIds ?? []).map((id: string) => ({ id, ...updatedData }));
    const sortObj = {
      searchText,
      pageIndex: 1,
      pageSize: Page_Size,
      type: 'samples',
      ...commonFilterParams,
    };
    const res = await (dispatch(saveBulkTrackingDates(bulkData)) as unknown as Promise<any>);
    setDateShow(false);
    if (res) {
      toasterMsg(t('samples:TAKING_DATES.SAMPLES_UPDATED_SUCCESS'), 'success');
      dispatch(setSampleSelectedIds([]));
      setClearSelectionToken((token) => token + 1);
    }
    await dispatch(getSampleRequestsTodoList(sortObj));
  };

  const refreshSampleList = async () => {
    const sortObj = {
      searchText,
      pageIndex: 1,
      pageSize: Page_Size,
      type: 'samples',
      ...commonFilterParams,
    };
    await dispatch(getSampleRequestsTodoList(sortObj));
  };

  const submitBulkPriority = async () => {
    const res = await (dispatch(saveBulkSamplePriority(sampleSelectedIds, copyPriority)) as unknown as Promise<any>);
    if (res) {
      toasterMsg(t('samples:TAKING_DATES.SAMPLES_UPDATED_SUCCESS'), 'success');
      dispatch(setSampleSelectedIds([]));
      setClearSelectionToken((token) => token + 1);
      await refreshSampleList();
      return true;
    }
    return false;
  };

  const exitBulkPriorityMode = () => {
    setBulkPriorityMode(false);
    setCopyPriority(null);
    setCapturedRowId(null);
  };

  const bulkPriorityClicked = async () => {
    if (!bulkPriorityMode) {
      setBulkStatusMenuOpen(false);
      setCopyPriority(null);
      setCapturedRowId(null);
      setBulkPriorityMode(true);
      return;
    }
    if (!copyPriority && sampleSelectedIds.length === 0) {
      exitBulkPriorityMode();
      return;
    }
    if (!copyPriority) {
      toasterMsg(t('samples:TAKING_DATES.SELECT_PRIORITY_TO_APPLY'), 'danger');
      return;
    }
    if (sampleSelectedIds.length === 0) {
      toasterMsg(t('samples:TAKING_DATES.SELECT_SAMPLES_TO_UPDATE'), 'danger');
      return;
    }
    const ok = await submitBulkPriority();
    if (ok) {
      exitBulkPriorityMode();
    }
  };

  const bulkStatusClicked = () => {
    if (sampleSelectedIds.length === 0) {
      toasterMsg(t('samples:TAKING_DATES.SELECT_SAMPLES_TO_UPDATE'), 'danger');
      return;
    }
    if (!selectedRows.every(isRowPending)) {
      toasterMsg(t('samples:TAKING_DATES.ONLY_PENDING_CAN_UPDATE'), 'danger');
      return;
    }
    if (bulkPriorityMode) exitBulkPriorityMode();
    setBulkStatusMenuOpen((open) => !open);
  };

  const applyBulkStatus = async (status: BulkSampleStatus) => {
    if (bulkStatusApplying || sampleSelectedIds.length === 0 || !status?._id) return;
    setBulkStatusApplying(true);
    const res = await (dispatch(saveBulkSampleStatus(sampleSelectedIds, status._id)) as unknown as Promise<any>);
    setBulkStatusApplying(false);
    if (res?.data) {
      const { updated = 0, failed = [] } = res.data;
      let summary = t('samples:TAKING_DATES.SAMPLES_STATUS_UPDATED', { count: updated });
      if (failed.length > 0) summary += ` ${t('samples:TAKING_DATES.SAMPLES_FAILED', { count: failed.length })}`;
      toasterMsg(summary, failed.length > 0 ? 'warning' : 'success');
      setBulkStatusMenuOpen(false);
      dispatch(setSampleSelectedIds([]));
      setClearSelectionToken((token) => token + 1);
      await refreshSampleList();
    }
  };

  React.useEffect(() => {
    if (!bulkStatusMenuOpen) return undefined;
    const close = (e: MouseEvent) => {
      if ((e.target as HTMLElement)?.closest?.('.bulk-status-menu') || (e.target as HTMLElement)?.closest?.('.bulk-status-trigger')) return;
      setBulkStatusMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [bulkStatusMenuOpen]);

  useEffect(() => {
    if (sampleSelectedIds.length === 0 && bulkStatusMenuOpen) setBulkStatusMenuOpen(false);
  }, [sampleSelectedIds.length, bulkStatusMenuOpen]);

  useEffect(() => {
    return () => {
      dispatch(filterSortData({ searchText: '' } as any));
    };
  }, [dispatch]);

  return (
    <>
      <div className="page-title-filter sticky samples-page-header">
        <div className="title-container d-flex align-items-center">
          <h4 className="title">{t('REPORTS.SAMPLE_TO_DO')}</h4>
        </div>
        <div className="search-filter samples-header-controls">
          {sampleTodoListTotalCount > 0 && (
            <span className="samples-header-count">
              {sampleTodoListTotalCount} {t('COSTING_TO_DO_LIST.TOTAL_RECORDS')}
            </span>
          )}
          {state.selectedCount > 0 && (
            <span className="samples-header-count samples-header-count--selected">
              {state.selectedCount} {t('COSTING_TO_DO_LIST.RECORD_SELECTED')}
            </span>
          )}
          <NewSearchBar placeholder={t('TAKING_DATES.SAMPLE_PRODUCT_REF')} onSearch={handleSearch} value={searchText} />
          <FilterSampleTodoList />
        </div>
      </div>

      <div className="scroll-container">
        <span className="top" />
        <span className="left" />
        <span className="right" />
        <div className="all-scroll">
          <div id="scrollableDiv" style={{ height: '100%', overflow: 'scroll' }}>
            <InfiniteScroll
              dataLength={sampleTodoList?.length || 0}
              hasMore={state?.hasMore}
              next={() => handlePageNextInScroll()}
              scrollableTarget="scrollableDiv"
              className="fixed-col-container"
              loader={<Loader />}
            >
              <List
                lengthEnabled={lengthEnabled}
                loading={state?.isLoading}
                getSelectedCount={getCount}
                bulkPriorityMode={bulkPriorityMode}
                onCapturePriority={handleCapturePriority}
                capturedRowId={capturedRowId}
                clearSelectionToken={clearSelectionToken}
              />
            </InfiniteScroll>
          </div>
        </div>
      </div>

      <div className="floting-actions">
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            className="btn"
            disabled={sampleTodoList?.length < 1}
            title={t('COSTING_TO_DO_LIST.EXPORT_EXCEL')}
            aria-label={t('COSTING_TO_DO_LIST.EXPORT_EXCEL')}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setExportMenuOpen((v) => !v);
            }}
          >
            <i className="icon-download f-26" />
          </button>
          {exportMenuOpen && (
            <div className="floating-export-menu">
              <button
                type="button"
                className={state.isDownloadLoading ? 'dropdown-item disabled-btn' : 'dropdown-item'}
                disabled={state.isDownloadLoading}
                onClick={() => {
                  exportFiles('pdf');
                }}
              >
                {state.isDownloadLoading ? t('PROGRESS_INDICATOR.DOWNLOADING') : t('PROGRESS_INDICATOR.DOWNLOAD_PDF')}
              </button>
              <button
                type="button"
                className={state.isExportLoading ? 'dropdown-item disabled-btn' : 'dropdown-item'}
                disabled={state.isExportLoading}
                onClick={() => {
                  exportFiles('excel');
                }}
              >
                {state.isExportLoading ? t('PROGRESS_INDICATOR.EXPORTING') : t('PROGRESS_INDICATOR.EXPORT_EXCEL')}
              </button>
            </div>
          )}
        </div>
        {sampleAddEditAccess && (
          <button
            type="button"
            className="btn"
            title={t('TAKING_DATES.UPDATE_SAMPLE')}
            aria-label={t('TAKING_DATES.UPDATE_SAMPLE')}
            onClick={showPopup}
            disabled={sampleSelectedIds.length === 0}
          >
            <i className="icon-location f-26" />
          </button>
        )}
        {canBulkUpdateSamples && (
          <button
            type="button"
            className={`btn ${bulkPriorityMode ? 'bulk-priority-active' : ''}`}
            title={t('samples:TAKING_DATES.BULK_UPDATE_PRIORITY')}
            aria-label={t('samples:TAKING_DATES.BULK_UPDATE_PRIORITY')}
            onClick={bulkPriorityClicked}
          >
            <i className="icon-clock f-26" />
          </button>
        )}
        {canBulkUpdateSamples && (
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              className={`btn bulk-status-trigger ${bulkStatusMenuOpen ? 'bulk-status-active' : ''}`}
              title={t('samples:TAKING_DATES.BULK_UPDATE_STATUS')}
              aria-label={t('samples:TAKING_DATES.BULK_UPDATE_STATUS')}
              onClick={bulkStatusClicked}
            >
              <i className="icon-Sample-Status f-26">
                <span className="path1" />
                <span className="path2" />
                <span className="path3" />
                <span className="path4" />
                <span className="path5" />
                <span className="path6" />
                <span className="path7" />
                <span className="path8" />
                <span className="path9" />
                <span className="path10" />
                <span className="path11" />
              </i>
            </button>
            {bulkStatusMenuOpen && (
              <div className="bulk-status-menu">
                {(statusList ?? [])
                  .filter((status) => status.refKey !== '-' && String(status.name || '').toLowerCase() !== 'pending')
                  .map((status) => (
                    <button
                      key={status._id}
                      type="button"
                      className={`bulk-status-menu__item ${resolveStatusBarClass(status.statusColor)}`}
                      title={status.name}
                      disabled={bulkStatusApplying}
                      onClick={() => applyBulkStatus(status)}
                    >
                      {status.name}
                    </button>
                  ))}
              </div>
            )}
          </div>
        )}
      </div>

      {dateShow && (
        <BulkDatesModal show={dateShow} toggle={() => setDateShow(false)} onSubmitForm={submitBulkSampleTracking} productBrand={bulkBrand as ProductBrand[]} />
      )}
    </>
  );
}

export default Samples;
